/**
 * CR227: Admin Config Debug Badge
 * 
 * Shows the active published snapshot/preset/template IDs for verification.
 * Only visible to admins or when ?admin=1 is present.
 * 
 * This component proves that the public site is rendering from the
 * published Layout Studio snapshot, not hardcoded values.
 * 
 * CR227 Enhancement: Added render_mode field to prove LayoutRenderer is used.
 */

import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Bug, RefreshCw, CheckCircle, AlertTriangle, ChevronDown, ChevronUp, Layout } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

interface PublicSiteConfig {
  timestamp: string;
  layout: {
    active_preset_id: string | null;
    preset_name: string | null;
    preset_updated_at: string | null;
    activation_updated_at: string | null;
    /** CR320: MD5 hash of layout_json for cache verification */
    layout_md5: string | null;
  };
  deal_card_templates: Array<{
    deal_type_key: string;
    template_id: string;
    updated_at: string | null;
  }>;
  site_state: {
    active_snapshot_id: string | null;
    active_batch_run_id: string | null;
    published_at: string | null;
    published_deal_count: number | null;
  } | null;
  config_hash: string;
}

interface AdminConfigBadgeProps {
  /** Optional: layout debug info from usePublicLayout for comparison */
  activePresetId?: string | null;
  presetName?: string | null;
  /** CR227: Rendered blocks count to verify LayoutRenderer is active */
  renderedBlocksCount?: number;
  /** CR228: List of rendered block IDs for verification */
  renderedBlockIds?: string[];
  /** CR227: Verified deals count from the public data source */
  dealsCount?: number;
  /** CR227: Ambiguous deals count */
  ambiguousDealsCount?: number;
  className?: string;
}

export function AdminConfigBadge({ 
  activePresetId, 
  presetName,
  renderedBlocksCount = 0,
  renderedBlockIds = [],
  dealsCount = 0,
  ambiguousDealsCount = 0,
  className = "" 
}: AdminConfigBadgeProps) {
  const { isAdmin } = useAuth();
  const [config, setConfig] = useState<PublicSiteConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  // Check if badge should be visible
  const hasAdminParam = typeof window !== "undefined" && 
    new URLSearchParams(window.location.search).get("admin") === "1";
  
  const showBadge = isAdmin || hasAdminParam;

  const fetchConfig = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const { data, error: fnError } = await supabase.functions.invoke("public-site-config", {
        method: "GET",
      });

      if (fnError) {
        console.error("[AdminConfigBadge] Fetch error:", fnError);
        setError(fnError.message);
        return;
      }

      setConfig(data as PublicSiteConfig);
    } catch (err) {
      console.error("[AdminConfigBadge] Unexpected error:", err);
      setError(String(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (showBadge) {
      fetchConfig();
    }
  }, [showBadge]);

  if (!showBadge) {
    return null;
  }

  const shortId = (id: string | null | undefined) => {
    if (!id) return "—";
    return id.length > 12 ? `${id.slice(0, 12)}…` : id;
  };

  const formatDate = (iso: string | null | undefined) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleString("en-US", { 
      month: "short", 
      day: "numeric", 
      hour: "numeric", 
      minute: "2-digit" 
    });
  };

  // Check if the loaded config matches what usePublicLayout resolved
  const configMatches = config && activePresetId 
    ? config.layout.active_preset_id === activePresetId
    : null;

  // CR227: Determine render mode - if we have rendered blocks, we're using LayoutRenderer
  const renderMode = renderedBlocksCount > 0 ? "layout_renderer" : "unknown";
  const isLayoutRendererActive = renderMode === "layout_renderer";

  return (
    <div className={`bg-slate-900 text-slate-100 rounded-lg border border-slate-700 shadow-lg ${className}`}>
      {/* Collapsed Header */}
      <div 
        className="px-4 py-2.5 flex items-center justify-between gap-4 cursor-pointer hover:bg-slate-800/50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <Bug className="w-4 h-4 text-amber-400" />
          <span className="font-semibold text-sm">Published Config</span>
          
          {/* Quick Status Indicators */}
          {config && !error && (
            <div className="flex items-center gap-2">
              {/* CR227: Layout Renderer verification badge */}
              {isLayoutRendererActive && (
                <Badge variant="outline" className="bg-blue-500/20 text-blue-400 border-blue-500/50 text-xs">
                  <Layout className="w-3 h-3 mr-1" />
                  LayoutRenderer
                </Badge>
              )}
              {configMatches === true && (
                <Badge variant="outline" className="bg-green-500/20 text-green-400 border-green-500/50 text-xs">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Verified
                </Badge>
              )}
              {configMatches === false && (
                <Badge variant="outline" className="bg-amber-500/20 text-amber-400 border-amber-500/50 text-xs">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  Mismatch
                </Badge>
              )}
              <span className="text-xs text-slate-400 font-mono">
                {config.layout.preset_name || shortId(config.layout.active_preset_id)}
              </span>
            </div>
          )}
          
          {error && (
            <Badge variant="outline" className="bg-red-500/20 text-red-400 border-red-500/50 text-xs">
              Error
            </Badge>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 text-slate-400 hover:text-slate-100"
            onClick={(e) => {
              e.stopPropagation();
              fetchConfig();
            }}
            disabled={loading}
          >
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
          </Button>
          {expanded ? (
            <ChevronUp className="w-4 h-4 text-slate-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-slate-400" />
          )}
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="px-4 py-3 border-t border-slate-700 space-y-3 text-xs font-mono">
          {error ? (
            <div className="text-red-400">
              Error loading config: {error}
            </div>
          ) : config ? (
            <>
              {/* CR227/228: Render Mode Verification - FIRST */}
              <div className="bg-slate-800/50 rounded p-2">
                <h4 className="text-slate-300 font-semibold mb-1.5">🔧 Render Mode (CR228)</h4>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-slate-400">
                  <div>render_mode:</div>
                  <div className={isLayoutRendererActive ? "text-green-400 font-bold" : "text-red-400 font-bold"}>
                    {renderMode}
                  </div>
                  <div>blocks_rendered:</div>
                  <div className="text-slate-200">{renderedBlocksCount}</div>
                  <div>deals_loaded:</div>
                  <div className="text-slate-200">{dealsCount} verified / {ambiguousDealsCount} ambiguous</div>
                  {renderedBlockIds.length > 0 && (
                    <>
                      <div>block_ids:</div>
                      <div className="text-cyan-400 text-xs">
                        {renderedBlockIds.join(", ")}
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Layout Section - CR320: Added layout_md5 for cache verification */}
              <div>
                <h4 className="text-slate-300 font-semibold mb-1.5">📐 Layout Preset</h4>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-slate-400">
                  <div>preset_id:</div>
                  <div className="text-slate-200 break-all">{config.layout.active_preset_id || "—"}</div>
                  <div>preset_name:</div>
                  <div className="text-cyan-400 font-semibold">{config.layout.preset_name || "—"}</div>
                  <div>updated_at:</div>
                  <div className="text-slate-200">{formatDate(config.layout.preset_updated_at)}</div>
                  <div>layout_md5:</div>
                  <div className="text-amber-400 font-mono">{config.layout.layout_md5 || "—"}</div>
                </div>
              </div>

              {/* Site State Section */}
              <div>
                <h4 className="text-slate-300 font-semibold mb-1.5">📦 Site State (Deals Snapshot)</h4>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-slate-400">
                  <div>snapshot_id:</div>
                  <div className="text-slate-200 break-all">{config.site_state?.active_snapshot_id || "—"}</div>
                  <div>deal_count:</div>
                  <div className="text-green-400">{config.site_state?.published_deal_count ?? "—"}</div>
                  <div>published_at:</div>
                  <div className="text-slate-200">{formatDate(config.site_state?.published_at)}</div>
                </div>
              </div>

              {/* Deal Card Templates Section */}
              <div>
                <h4 className="text-slate-300 font-semibold mb-1.5">🎴 Deal Card Templates</h4>
                <div className="flex flex-wrap gap-2">
                  {config.deal_card_templates.length > 0 ? (
                    config.deal_card_templates.map((t) => (
                      <span
                        key={t.deal_type_key}
                        className="px-2 py-0.5 bg-slate-800 rounded text-slate-300"
                        title={`Updated: ${formatDate(t.updated_at)}`}
                      >
                        {t.deal_type_key}
                      </span>
                    ))
                  ) : (
                    <span className="text-slate-500">No templates found</span>
                  )}
                </div>
              </div>

              {/* Config Hash */}
              <div className="pt-2 border-t border-slate-700 flex items-center justify-between text-slate-400">
                <span>config_hash:</span>
                <span className="text-amber-400 font-semibold">{config.config_hash}</span>
              </div>

              {/* Comparison with current render */}
              {activePresetId && (
                <div className="pt-2 border-t border-slate-700">
                  <h4 className="text-slate-300 font-semibold mb-1.5">🔍 Render Verification</h4>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-slate-400">
                    <div>rendered_preset:</div>
                    <div className="text-slate-200 break-all">{activePresetId || "—"}</div>
                    <div>match:</div>
                    <div className={configMatches ? "text-green-400" : "text-red-400"}>
                      {configMatches ? "✓ Matches published" : "✗ Does not match!"}
                    </div>
                  </div>
                </div>
              )}

              {/* Timestamp */}
              <div className="pt-2 text-slate-500 text-center">
                Fetched: {formatDate(config.timestamp)}
              </div>
            </>
          ) : (
            <div className="text-slate-400">Loading...</div>
          )}
        </div>
      )}
    </div>
  );
}
